// Hook for horizontal drag-to-resize
import { useCallback, useEffect, useRef, useState } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// `snap` ({ threshold, width }) lets a pane collapse below its resizable range:
// any target width under `threshold` resolves to exactly `snap.width` instead of
// clamping to minWidth, so dragging past the threshold snaps the pane into its
// condensed form (and back out once the pointer crosses the threshold again).
export function useHorizontalResize({ initialWidth, minWidth, maxWidth, direction, onWidthChange, snap = null }) {
  const [width, setWidth] = useState(() => {
    if (initialWidth === 0) return 0;
    if (snap && initialWidth < snap.threshold) return snap.width;
    return clamp(initialWidth, minWidth, maxWidth);
  });
  const [isResizing, setIsResizing] = useState(false);

  const startXRef = useRef(0);
  const widthOnDragStartRef = useRef(width);
  const isResizingRef = useRef(false);
  const widthRef = useRef(width);
  const bodyUserSelectRef = useRef(null);
  const minWidthRef = useRef(minWidth);
  const maxWidthRef = useRef(maxWidth);
  const snapRef = useRef(snap);

  useEffect(() => {
    snapRef.current = snap;
  }, [snap]);

  const resolveWidth = useCallback((targetWidth) => {
    const currentSnap = snapRef.current;
    if (currentSnap && targetWidth < currentSnap.threshold) return currentSnap.width;
    return clamp(targetWidth, minWidthRef.current, maxWidthRef.current);
  }, []);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    minWidthRef.current = minWidth;
  }, [minWidth]);

  useEffect(() => {
    maxWidthRef.current = maxWidth;
  }, [maxWidth]);

  const handlePointerMove = useCallback(
    (event) => {
      if (!isResizingRef.current) return;

      const delta = direction === 'right' ? event.clientX - startXRef.current : startXRef.current - event.clientX;

      setWidth(resolveWidth(widthOnDragStartRef.current + delta));
    },
    [direction, resolveWidth],
  );

  const handlePointerUp = useCallback(
    function onPointerUp() {
      if (!isResizingRef.current) return;

      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', onPointerUp);

      if (typeof document !== 'undefined' && bodyUserSelectRef.current !== null) {
        document.body.style.userSelect = bodyUserSelectRef.current;
        bodyUserSelectRef.current = null;
      }

      isResizingRef.current = false;
      setIsResizing(false);
    },
    [handlePointerMove],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      if (typeof document !== 'undefined' && bodyUserSelectRef.current !== null) {
        document.body.style.userSelect = bodyUserSelectRef.current;
        bodyUserSelectRef.current = null;
      }
    };
  }, [handlePointerMove, handlePointerUp]);

  const handlePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      startXRef.current = event.clientX;
      widthOnDragStartRef.current = widthRef.current;
      isResizingRef.current = true;
      setIsResizing(true);

      if (typeof document !== 'undefined') {
        bodyUserSelectRef.current = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
      }

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp],
  );

  const setWidthDirectly = useCallback(
    (newWidth) => {
      const clampedWidth = newWidth === 0 ? 0 : resolveWidth(newWidth);
      setWidth(clampedWidth);
      widthRef.current = clampedWidth;
    },
    [resolveWidth],
  );

  const getHandleProps = useCallback((disabled = false) => (disabled ? {} : { onPointerDown: handlePointerDown }), [handlePointerDown]);

  useEffect(() => {
    if (onWidthChange) {
      onWidthChange(width);
    }
  }, [width, onWidthChange]);

  return {
    width,
    isResizing,
    setWidth: setWidthDirectly,
    getHandleProps,
    handleProps: getHandleProps(),
  };
}
